/**
 * PostHog initialization with privacy-first defaults.
 *
 * Autocapture is disabled -- we use explicit tracking via the SDK.
 * Pageview capture is disabled -- handled by GTM.
 * Session recording masks all inputs by default.
 */

import posthog from 'posthog-js';
import { getAnalyticsConfig } from './config';

let _initialized = false;

export function initPostHog(): typeof posthog {
  if (_initialized) return posthog;

  const config = getAnalyticsConfig();

  // On localhost, force the Development PostHog key regardless of .env config.
  // This prevents local dev traffic from polluting staging/production and also
  // provides a fallback if VITE_PUBLIC_POSTHOG_KEY is not set at all.
  const POSTHOG_DEV_KEY = 'phc_U4Ice0FPaajUGflcJX0uBTeRBGzybVwdwoDD6eBLjRS';
  let activeKey = config.posthogKey;

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
    if (isLocal && activeKey !== POSTHOG_DEV_KEY) {
      if (activeKey) {
        console.warn('[Analytics] Localhost detected -- forcing Development PostHog key to prevent staging/production pollution.');
      }
      activeKey = POSTHOG_DEV_KEY;
    }
  }

  if (!activeKey) return posthog;

  posthog.init(activeKey, {
    api_host: config.posthogHost,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-sensitive]',
    },
    loaded: (ph) => {
      if (config.environment === 'development') {
        ph.debug();
      }
    },
  });

  _initialized = true;
  return posthog;
}

/** True once `posthog.init` has run (REK-3389: init is deferred until analytics consent). */
export function isPostHogInitialized(): boolean {
  return _initialized;
}

/**
 * REK-3389: the single entry point for turning PostHog on after analytics
 * consent. Initializes the SDK on first call (setting its cookie/localStorage
 * persistence only now, never before consent) and then opts in to capturing.
 * Safe to call repeatedly. No-op if no PostHog key is configured.
 */
export function optInPostHog(): void {
  const ph = initPostHog();
  if (!_initialized) return;
  ph.opt_in_capturing();
}

/**
 * REK-3389: opt out of capturing. If PostHog was never initialized (no
 * analytics consent was ever given on this device) there is nothing to opt
 * out of and we must NOT init it just to switch it off.
 */
export function optOutPostHog(): void {
  if (!_initialized) return;
  posthog.opt_out_capturing();
}

/** Test-only: reset the module-level init latch. */
export function __resetPostHogInitForTesting(): void {
  _initialized = false;
}

export function getPostHog(): typeof posthog {
  return posthog;
}
